import { type ReactNode, useId } from "react";
import { Modal } from "./Modal.tsx";
import i18n from "../util/i18n.ts";

// =============================================================================
// MobileSheet — bottom-sheet overlay for the mobile game-UI paradigm.
// Marker: MOBILE_SHEET_CONTRACT_V1 (do not remove — the source-grep gate keys
// off this string to prove the boundary convention is documented here).
// -----------------------------------------------------------------------------
// BOUNDARY CONTRACT (encoded, enforced by mobileM2SheetOverlay.test.ts):
//
// 1. Sheets/overlays NEVER enter the navigation back stack. A sheet is pure
//    component state (an `open` boolean owned by the host view). Opening or
//    closing a sheet changes no URL and pushes no history entry, so the global
//    back control can neither close nor reopen a sheet — back always acts on the
//    underlying page, exactly as if the sheet were not there. Accordingly this
//    component mutates no history/pushState/location and writes no m_return /
//    m_src return-stack keys (asserted by the gate).
//
// 2. TWO exit paths only:
//      - close: `onClose()` dismisses the sheet in place (backdrop click and the
//        explicit close button both call it). The host view stays put.
//      - navigate: a link / CTA rendered *inside* the sheet (typically in the
//        footer) navigates away; the sheet unmounts together with the view. Any
//        such link that leaves the current tab must be seeded by the CALLER via
//        createMobileCrossTabHref — the sheet itself never seeds return state, so
//        that concern lives with each adopter, not here.
//
// 3. Direct-URL / deep-link surfaces are out of scope for this component. A page
//    that a sheet replaces must keep a real page fallback or make an explicit
//    landing decision at the URL level; that is per-page M3 work, decided where
//    the sheet is mounted, not inside MobileSheet.
//
// IMPLEMENTATION NOTES:
//   - Built ON TOP of the shared react-bootstrap Modal machinery (./Modal.tsx):
//     its ModalManager, portal and focus trap are reused; animation stays
//     disabled there because react-bootstrap's JS transition can leave Android
//     WebView modals stuck open. The slide-up here is a CSS-only *entrance*
//     keyframe (`.mobile-sheet-content`) with no exit transition and no JS in
//     the unmount path, so it cannot reproduce the stuck-open failure; it is
//     also suppressed under prefers-reduced-motion. When motion is off the
//     result degrades to a static bottom-anchored panel with a backdrop, which
//     is the acceptable fallback.
//   - Theming is handled via the shared Bootstrap/SCSS variables (see the
//     `.mobile-sheet-*` family in light.scss); dark mode falls out of the
//     per-theme SASS variable redefinitions (dark.scss) with no component-level
//     branching, matching the neighbouring `.mobile-*` blocks.
// =============================================================================

export const MobileSheet = ({
	open,
	onClose,
	title,
	children,
	footer,
}: {
	open: boolean;
	onClose: () => void;
	title: ReactNode;
	children: ReactNode;
	footer?: ReactNode;
}) => {
	const titleId = useId();

	return (
		<Modal
			aria-labelledby={titleId}
			className="mobile-sheet"
			contentClassName="mobile-sheet-content"
			dialogClassName="mobile-sheet-dialog"
			onHide={onClose}
			scrollable
			show={open}
		>
			{/* G4-1: the cosmetic drag grabber was removed — it advertised a
			    drag-to-dismiss affordance the sheet never had. Dismissal is the
			    header X or a backdrop tap (see the boundary contract above). */}
			<Modal.Header
				className="mobile-sheet-header border-0"
				closeButton
				closeLabel={i18n.t("common.close", {
					ns: "ui",
					defaultValue: "Close",
				})}
			>
				<Modal.Title className="mobile-sheet-title" id={titleId}>{title}</Modal.Title>
			</Modal.Header>
			<Modal.Body className="mobile-sheet-body">{children}</Modal.Body>
			{footer !== undefined ? (
				<Modal.Footer className="mobile-sheet-footer">{footer}</Modal.Footer>
			) : null}
		</Modal>
	);
};
